import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { Stethoscope, PaperPlaneTilt, User, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

function makeSession() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

const STARTERS = [
  "I've had a mild fever and body ache for two days. What could it be?",
  "I have this lab report: hemoglobin 11.2, wbc 8.4, platelets 190. Anything to worry about?",
  "My child (age 4) has been coughing at night. Home care tips?",
  "I get frequent headaches around exam time. How to manage?",
];

export default function DoctorChat() {
  const { user } = useAuth();
  const [sessionId] = useState(makeSession());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg) return;
    if (!user || user === false) { toast.error("Please log in to chat"); return; }
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setBusy(true);
    try {
      const { data } = await api.post("/doctor/chat", { session_id: sessionId, message: msg });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Chat failed");
      setMessages((m) => [...m, { role: "assistant", text: "I'm unable to respond right now. Please try again shortly." }]);
    } finally { setBusy(false); }
  };

  if (!user || user === false) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <Stethoscope size={40} className="mx-auto text-[#2C4A3B]" />
        <h1 className="font-heading text-4xl mt-4">Dr. Anveshan is ready when you are</h1>
        <p className="text-[#5C564D] mt-3">Please log in to start a confidential health conversation.</p>
        <Link to="/login" data-testid="doctor-login-cta" className="samaaj-btn-primary mt-6 inline-block">Log in to continue</Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#2C4A3B]">Doctor chat</div>
      <h1 className="font-heading text-5xl mt-2 tracking-tight">A doctor by your side, <em className="text-[#2C4A3B] not-italic">day or night</em>.</h1>
      <p className="text-[#5C564D] mt-2 max-w-2xl">Describe symptoms or paste report notes. Dr. Anveshan (powered by Claude Sonnet) offers plain-language guidance and flags anything urgent.</p>

      <div className="mt-8 samaaj-card p-6 min-h-[520px] flex flex-col" data-testid="chat-container">
        {messages.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: "#2C4A3B" }}>
              <Stethoscope size={28} color="#F9F6F0" weight="duotone" />
            </div>
            <div className="font-heading text-2xl mt-4">How can I help today?</div>
            <div className="text-sm text-[#5C564D] mt-2">Try one of these to get started:</div>
            <div className="mt-4 grid sm:grid-cols-2 gap-2 max-w-2xl">
              {STARTERS.map((s, i) => (
                <button key={i} data-testid={`starter-${i}`} onClick={() => send(s)} className="text-left text-sm p-3 rounded-2xl border border-[#E5D9CC] bg-[#F9F6F0] hover:bg-[#EFE4D3]" style={{ transition: "background-color 180ms ease-out" }}>
                  <Sparkle size={12} weight="fill" className="inline text-[#D49A36] mr-1" /> {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex-1 space-y-4 overflow-y-auto" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: m.role === "user" ? "#D95A41" : "#2C4A3B" }}>
                  {m.role === "user" ? <User size={16} color="#F9F6F0" /> : <Stethoscope size={16} color="#F9F6F0" />}
                </div>
                <div className={`px-4 py-3 max-w-[80%] whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bubble-user" : "bubble-ai"}`}>{m.text}</div>
              </div>
            ))}
            {busy && (
              <div className="flex gap-3">
                <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: "#2C4A3B" }}>
                  <Stethoscope size={16} color="#F9F6F0" />
                </div>
                <div className="bubble-ai px-4 py-3 text-sm text-[#5C564D]">Thinking…</div>
              </div>
            )}
            <div ref={endRef} />
          </div>
        )}

        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-4 flex gap-2">
          <input
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Describe your symptoms or paste a report snippet…"
            className="flex-1 px-4 py-3 rounded-full border border-[#E5D9CC] bg-[#F9F6F0] focus:outline-none focus:border-[#2C4A3B]"
          />
          <button data-testid="chat-send" disabled={busy || !input.trim()} type="submit" className="samaaj-btn-primary flex items-center gap-2">
            <PaperPlaneTilt size={16} weight="fill" /> Send
          </button>
        </form>
      </div>

      <div className="mt-4 text-xs text-[#5C564D]">This is general guidance, not a diagnosis. For emergencies, please contact a licensed physician immediately.</div>
    </div>
  );
}
